import { parseSsrsDate } from './ssrsDate';

function formatSsrsDate(isoDate: string, time: string): string {
  const [yyyy, mm, dd] = isoDate.split('-');
  return `${dd}/${mm}/${yyyy} ${time}`;
}

/**
 * A real date picker over a value the report server insists on seeing as dd/MM/yyyy.
 *
 * Anything already stored that this cannot parse — an expression, a format from some other tool —
 * stays a plain text box. Rewriting a value we do not understand is the one outcome worse than the
 * free-text box this replaces.
 *
 * Only two things get said out loud, and both say something the box cannot. That the value is not
 * in the server's format, so the picker stood down rather than rewrote it; and that a stored time
 * of day is being carried through, which is invisible in a date-only control and would change what
 * the report runs with if it were dropped. A parseable midnight date says nothing at all.
 */
export function DateField({
  id,
  value,
  onChange,
  disabled,
  allowEmpty
}: {
  id: string;
  value: string;
  onChange: (v: string[]) => void;
  disabled: boolean;
  allowEmpty: boolean;
}) {
  const parsed = value === '' ? null : parseSsrsDate(value);
  const unparseable = value !== '' && parsed === null;

  if (unparseable) {
    return (
      <>
        <input id={id} className="input" disabled={disabled} value={value} onChange={e => onChange([e.target.value])} />
        <div className="param-note">
          Not the report server&rsquo;s dd/mm/yyyy format, so it is left as text rather than changed
          for you.
        </div>
      </>
    );
  }

  // The stored time of day is kept as it was. Several live subscriptions carry a real timestamp
  // rather than midnight, and silently zeroing it would change what the report runs with.
  const time = parsed?.time ?? '00:00:00';

  return (
    <>
      <input
        id={id}
        className="input"
        type="date"
        disabled={disabled}
        value={parsed?.date ?? ''}
        onChange={e => onChange(e.target.value === '' ? [] : [formatSsrsDate(e.target.value, time)])}
      />
      {value !== '' && !allowEmpty && time !== '00:00:00' && (
        <div className="param-note">
          Sent as <code>{value}</code> — the time of day is kept as it was stored.
        </div>
      )}
    </>
  );
}
